import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProjects } from "../contexts/ProjectContext";
import { ChevronUp, ChevronDown, Eye } from "lucide-react";
import api from "../utils/apiClient";

const ContentHistory = () => {
  const navigate = useNavigate();
  const { currentProject } = useProjects();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [sortDirection, setSortDirection] = useState("desc"); 

  useEffect(() => {
    if (currentProject?.id) {
      fetchContent();
    }
  }, [currentProject?.id]); 

  const fetchContent = async () => {
    if (!currentProject?.id) return;
    
    try {
      setLoading(true);
      
      // Fetch regular generated content
      const res = await api.generatedKeywords.list(currentProject.id, {
        includeDeleted: false,
      });
      
      // Filter only generated content
      const generatedKeywords = res.data.filter(keyword => keyword.status === "generated");
      
      // Fetch blog content for each generated keyword
      const contentPromises = generatedKeywords.map(async (keyword) => {
        try {
          const blogRes = await api.blogGeneration.getBlogByKeyword(keyword.id);
          return {
            id: keyword.id,
            title: blogRes.data.title,
            keyword: keyword.keyword,
            scheduled_for: new Date(keyword.scheduled_for),
            publishedDate: keyword.published_at ? new Date(keyword.published_at) : new Date(keyword.scheduled_for),
            type: 'keyword'
          };
        } catch (error) {
          console.error(`Error fetching blog content for keyword ${keyword.id}:`, error);
          return null;
        }
      });

      // Fetch competitor generated content
      let competitorContent = [];
      try {
        const competitorRes = await api.blogGeneration.listCompetitorContent(currentProject.id);
        competitorContent = competitorRes.data.map(item => ({
          id: item.id,
          title: item.title,
          keyword: item.source_url,
          publishedDate: new Date(item.created_at),
          type: 'competitor'
        }));
      } catch (error) {
        console.error("Error fetching competitor content:", error);
      }

      // Wait for all blog content to be fetched
      const transformedContent = [
        ...(await Promise.all(contentPromises)).filter(content => content !== null),
        ...competitorContent
      ];
      
      setContent(transformedContent);
    } catch (error) {
      console.error("Error fetching content history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewContent = (item) => {
    if (item.type === 'competitor') {
      navigate(`/content/${item.id}`);
    } else {
      navigate(`/dashboard/content/${item.id}`);
    }
  };

  const toggleSortDirection = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  // Sort content by published date based on sort direction
  const sortedContent = [...content].sort((a, b) => {
    const dateA = new Date(a.publishedDate);
    const dateB = new Date(b.publishedDate);
    return sortDirection === "asc" 
      ? dateA - dateB 
      : dateB - dateA;
  });

  return (
    <div className="w-full p-4 lg:pl-6 lg:pr-6 bg-white">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Content History</h1>
          <p className="text-sm text-gray-500 mt-1">View your published content</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keyword/Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center cursor-pointer" onClick={toggleSortDirection}>
                      Published Date
                      <button className="ml-1 focus:outline-none">
                        {sortDirection === "asc" ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedContent.length > 0 ? (
                  sortedContent.map((item) => (
                    <tr 
                      key={item.id} 
                      className="hover:bg-gray-50 bg-white"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <a 
                          onClick={() => handleViewContent(item)}
                          className="text-black hover:text-primary-800 cursor-pointer hover:underline"
                        >
                          {item.title}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.type === 'competitor' ? (
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full">
                            Competitor
                          </span>
                        ) : (
                          item.keyword
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.publishedDate.toLocaleDateString('en-US', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleViewContent(item)}
                          className="bg-primary-500 text-white px-3 py-1 rounded hover:bg-primary-600 text-sm font-medium transition flex items-center justify-center"
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="bg-white">
                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">
                      No published content found yet. Content will appear here after it has been generated and published.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentHistory;